import { Checkbox } from '@design-system/components/checkbox/checkbox';
import { Typography } from '@design-system/components/typography/typography';
import { sectionWrapper, pricesWrapper } from './exclusivePriceSetup.css';
import { InputPrice } from '@design-system/components/input/inputPrice';
import { faDollar } from '@fortawesome/pro-regular-svg-icons';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { Divider } from '@mui/material';

type PriceSection = {
  price?: string;
  selected: boolean;
  hasError?: boolean;
  changePrice: (price?: string) => void;
  selectType: () => void;
  label: string;
  initialized: boolean;
};

export type ExclusivePriceSetupProps = {
  monthly: PriceSection;
  annual: PriceSection;
  sectionClassName?: string;
  readonly: boolean;
  disabled?: boolean;
  submitting?: boolean;
};

export const ExclusivePriceSetup = ({
  monthly,
  annual,
  sectionClassName,
  readonly,
  disabled,
}: ExclusivePriceSetupProps) => {
  return (
    <div
      className={clsx(pricesWrapper, readonly && sprinkles({ width: '100%' }))}
    >
      <ExclusivePriceSection
        {...monthly}
        sectionClassName={sectionClassName}
        readonly={readonly}
        type="monthly"
        disabled={disabled}
        supportingText="Enter price in USD"
        checkboxTestId="enableMonthly"
        inputTestId="monthlyPrice"
      />
      {readonly ? <Divider className="w-full" /> : null}
      <ExclusivePriceSection
        {...annual}
        sectionClassName={sectionClassName}
        readonly={readonly}
        type="annual"
        disabled={disabled}
        supportingText="Enter price in USD"
        checkboxTestId="enableAnnual"
        inputTestId="annualPrice"
      />
    </div>
  );
};

export const ExclusivePriceSection = ({
  price,
  selected,
  selectType,
  changePrice,
  label,
  hasError,
  inputTestId,
  checkboxTestId,
  sectionClassName,
  readonly,
  type,
  initialized,
  disabled,
  supportingText,
}: PriceSection & {
  readonly: boolean;
  sectionClassName?: string;
  type: 'monthly' | 'annual';
  disabled?: boolean;
  supportingText: string;
  inputTestId?: string;
  checkboxTestId?: string;
}) => {
  if (readonly && initialized) {
    const details = getPriceDetails({ type, price });
    return (
      <div className={clsx(sectionWrapper, sectionClassName)}>
        <Typography>{details}</Typography>
      </div>
    );
  }
  return (
    <div className={clsx(sectionWrapper, sectionClassName)}>
      {readonly && !initialized ? (
        <Typography>
          You can still offer {type} plan. Check option below.
        </Typography>
      ) : null}
      <div className="flex flex-col gap-2">
        <div className="flex items-center">
          <Checkbox
            checked={selected}
            onChange={selectType}
            wrapperClassName="p-4"
            testId={checkboxTestId}
            disabled={disabled}
          />
          <Typography>{label}</Typography>
        </div>
        <div className="w-[200px] pl-[44px]">
          <InputPrice
            disabled={!selected || disabled}
            value={price}
            onChange={changePrice}
            startIcon={{
              icon: faDollar,
              className: sprinkles({ color: 'N-80' }),
            }}
            supportingText={supportingText}
            placeholder="00.00"
            hasError={hasError}
            testId={inputTestId}
          />
        </div>
      </div>
    </div>
  );
};

const getPriceDetails = ({
  type,
  price = 'xx',
}: {
  type: 'monthly' | 'annual';
  price?: string;
}) => {
  if (type == 'monthly')
    return `Monthly Subscription price is set to $${price}/month`;
  return `Annual Subscription price is set to $${price}/year`;
};
